import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Tag } from "lucide-react";
import { toast } from "sonner";
import api, { imgUrl, formatApiErrorDetail } from "../../lib/api";
import { ImageUpload } from "../../components/admin/ImageUpload";

const empty = { name: "", logo: "", url: "" };

export default function AdminBrands() {
  const [brands, setBrands] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/brands").then(({ data }) => setBrands(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (b) => { setEditing(b); setForm({ name: b.name, logo: b.logo || "", url: b.url || "" }); setOpen(true); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Συμπληρώστε όνομα"); return; }
    setSaving(true);
    try {
      if (editing) await api.put(`/brands/${editing.id}`, form);
      else await api.post("/brands", form);
      toast.success(editing ? "Ενημερώθηκε" : "Προστέθηκε");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Σφάλμα"); }
    finally { setSaving(false); }
  };

  const del = async (b) => {
    if (!window.confirm(`Διαγραφή της μάρκας «${b.name}»;`)) return;
    await api.delete(`/brands/${b.id}`);
    toast.success("Διαγράφηκε"); load();
  };

  const inputCls = "w-full px-3 py-2.5 rounded-sm border border-[#E5E9E4] bg-white text-sm focus:border-[#1E3A2F] focus:ring-1 focus:ring-[#1E3A2F] outline-none";

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-serif-display text-3xl text-[#1E3A2F]">Μάρκες</h1>
        <button onClick={openNew} data-testid="add-brand-button" className="inline-flex items-center gap-2 bg-[#1E3A2F] text-white px-4 py-2.5 rounded-sm text-sm font-medium hover:bg-[#111C18]">
          <Plus className="h-4 w-4" /> Νέα μάρκα
        </button>
      </div>
      <p className="text-[#536D61] mb-6 text-sm">Οι μάρκες εμφανίζονται σε ειδική ενότητα στην αρχική σελίδα.</p>

      {brands.length ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {brands.map((b) => (
            <div key={b.id} className="bg-white border border-[#E5E9E4] rounded-sm p-4 text-center" data-testid={`brand-row-${b.id}`}>
              <div className="h-20 flex items-center justify-center mb-3">
                {b.logo ? <img src={imgUrl(b.logo)} alt={b.name} className="max-h-16 max-w-full object-contain" /> : <Tag className="h-8 w-8 text-[#1E3A2F]/40" />}
              </div>
              <p className="font-medium text-[#1E3A2F] text-sm">{b.name}</p>
              <div className="flex justify-center gap-2 mt-3">
                <button onClick={() => openEdit(b)} data-testid={`edit-brand-${b.id}`} className="p-2 text-[#536D61] hover:text-[#1E3A2F]"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => del(b)} data-testid={`delete-brand-${b.id}`} className="p-2 text-[#C25953] hover:text-[#a3433d]"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-dashed border-[#E5E9E4] rounded-sm py-16 text-center text-[#536D61]">Δεν υπάρχουν μάρκες ακόμη.</div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-auto bg-black/50">
          <div className="bg-white rounded-sm w-full max-w-md my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E9E4]">
              <h2 className="font-serif-display text-2xl text-[#1E3A2F]">{editing ? "Επεξεργασία μάρκας" : "Νέα μάρκα"}</h2>
              <button onClick={() => setOpen(false)}><X className="h-5 w-5 text-[#536D61]" /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#1E3A2F] mb-1.5">Όνομα *</label>
                <input data-testid="brand-name" value={form.name} onChange={set("name")} className={inputCls} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1E3A2F] mb-1.5">Σύνδεσμος (URL) — προαιρετικό</label>
                <input value={form.url} onChange={set("url")} placeholder="https://..." className={inputCls} />
              </div>
              <ImageUpload value={form.logo} onChange={(v) => setForm((f) => ({ ...f, logo: v }))} label="Λογότυπο μάρκας" testid="brand-logo-upload" />
              <div className="flex justify-end gap-3 pt-4 border-t border-[#E5E9E4]">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2.5 rounded-sm border border-[#E5E9E4] text-sm">Ακύρωση</button>
                <button type="submit" disabled={saving} data-testid="save-brand-button" className="px-5 py-2.5 rounded-sm bg-[#1E3A2F] text-white text-sm font-medium hover:bg-[#111C18] disabled:opacity-60">{saving ? "Αποθήκευση..." : "Αποθήκευση"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
